import React from 'react';

export type IconName =
  // DS sidebar icons (18×18 viewBox)
  | 'home'
  | 'email'
  | 'leads'
  | 'contacts'
  | 'projects'
  | 'settings'
  | 'search'
  | 'facturacion'
  | 'messages'
  // DS special viewBox
  | 'chevron-down'
  | 'bolt-large'
  | 'activity-large'
  | 'envelope-large'
  | 'contacts-large'
  | 'settings-large'
  | 'projects-large'
  | 'facturacion-large'
  | 'messages-large'
  // Legacy aliases (map to DS icons)
  | 'envelope'
  | 'bolt'
  | 'person'
  // Legacy utility icons (24×24 viewBox)
  | 'close'
  | 'return'
  | 'forward'
  | 'ok'
  | 'error'
  | 'arrow-right'
  | 'flag'
  | 'inbox'
  | 'activity'
  | 'send'
  | 'compose'
  | 'alert'
  | 'plus'
  // Social network icons (18×18 viewBox)
  | 'social-facebook'
  | 'social-instagram'
  | 'social-twitter'
  | 'social-linkedin'
  | 'social-youtube'
  | 'social-tiktok'
  // Editor / utility
  | 'link'
  | 'paperclip'
  | 'list-bullet'
  | 'list-ordered'
  | 'blockquote'
  | 'code'
  | 'pencil'
  | 'chevron-left'
  | 'sparkle'
  // Voice (Fase 4)
  | 'mic'
  | 'volume'
  | 'volume-mute'
  | 'stop';

interface IconDef {
  vb: string;
  el: React.ReactNode;
}

const ICONS: Record<IconName, IconDef> = {
  // ── DS sidebar icons (18×18) ──────────────────────────────────────────────
  home: {
    vb: '0 0 18 18',
    el: <path d="M3 7.5 9 3l6 4.5V15a1 1 0 0 1-1 1h-3v-5H7v5H4a1 1 0 0 1-1-1V7.5Z" />,
  },
  email: {
    vb: '0 0 18 18',
    el: (
      <>
        <rect x="2.5" y="4" width="13" height="10" rx="1" />
        <path d="m3 5 6 4.5L15 5" />
      </>
    ),
  },
  leads: {
    vb: '0 0 18 18',
    el: <path d="M10 2 4 10h4l-2 6 6-8H8l2-6Z" />,
  },
  contacts: {
    vb: '0 0 18 18',
    el: (
      <>
        <circle cx="9" cy="6.5" r="3" />
        <path d="M3.5 15.5c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
      </>
    ),
  },
  projects: {
    vb: '0 0 18 18',
    el: <path d="M2 2h6v6H2zM10 2h6v6h-6zM2 10h6v6H2zM10 10h6v6h-6z" />,
  },
  settings: {
    vb: '0 0 18 18',
    el: (
      <>
        <circle cx="9" cy="9" r="2.25" />
        <path d="M14.5 9a5.5 5.5 0 0 0-.1-1l1.5-1.1-1.5-2.6-1.7.7a5.5 5.5 0 0 0-1.7-1L10.5 2h-3l-.5 2a5.5 5.5 0 0 0-1.7 1l-1.7-.7-1.5 2.6L3.6 8a5.5 5.5 0 0 0 0 2l-1.5 1.1 1.5 2.6 1.7-.7a5.5 5.5 0 0 0 1.7 1l.5 2h3l.5-2a5.5 5.5 0 0 0 1.7-1l1.7.7 1.5-2.6L14.4 10a5.5 5.5 0 0 0 .1-1Z" />
      </>
    ),
  },
  search: {
    vb: '0 0 18 18',
    el: (
      <>
        <circle cx="8" cy="8" r="5" />
        <path d="m15 15-3.5-3.5" />
      </>
    ),
  },
  messages: {
    vb: '0 0 18 18',
    el: (
      <>
        <path d="M2.5 4.5h13v8h-7.5L4.5 15v-2.5H2.5v-8Z" />
        <path d="M5.5 7.5h8M5.5 9.75h5.5" />
      </>
    ),
  },
  // Real Ikonate glyph (dolar.svg, MIT), unmodified — vb kept at Ikonate's
  // native 24×24 rather than force-fit to the sidebar's usual 0 0 18 18, so
  // the path data isn't touched at all. The <Icon> wrapper's width/height
  // props scale it correctly regardless of viewBox.
  facturacion: {
    vb: '0 0 24 24',
    el: <path d="M12 4L12 6M12 18L12 20M15.5 8C15.1666667 6.66666667 14 6 12 6 9 6 8.5 7.95652174 8.5 9 8.5 13.140327 15.5 10.9649412 15.5 15 15.5 16.0434783 15 18 12 18 10 18 8.83333333 17.3333333 8.5 16" />,
  },

  // ── DS large variants ─────────────────────────────────────────────────────
  'chevron-down': {
    vb: '0 0 14 8',
    el: <path d="m1 1 6 6 6-6" />,
  },
  'bolt-large': {
    vb: '0 0 56 56',
    el: <path d="M30 4 12 30h12L20 52l22-28H28l4-20Z" />,
  },
  'activity-large': {
    vb: '0 0 56 56',
    el: <polyline points="5 28 14 28 21 7 35 49 42 28 51 28" />,
  },
  'envelope-large': {
    vb: '0 0 56 56',
    el: (
      <>
        <rect x="6" y="14" width="44" height="32" rx="1" />
        <path d="m7 16 21 14L49 16" />
      </>
    ),
  },
  'contacts-large': {
    vb: '0 0 56 56',
    el: (
      <>
        <circle cx="28" cy="20" r="9" />
        <path d="M11 48c0-9 7-16 17-16s17 7 17 16" />
      </>
    ),
  },
  'settings-large': {
    vb: '0 0 56 56',
    el: (
      <>
        <circle cx="28" cy="28" r="7" />
        <path d="M45 28a17 17 0 0 0-.3-3l4.7-3.4-4.7-8-5.3 2.2a17 17 0 0 0-5.3-3L33 6h-9l-1.6 6a17 17 0 0 0-5.3 3l-5.3-2.2-4.7 8L11 25a17 17 0 0 0 0 6l-4.7 3.4 4.7 8 5.3-2.2a17 17 0 0 0 5.3 3l1.6 6h9l1.6-6a17 17 0 0 0 5.3-3l5.3 2.2 4.7-8L45 31a17 17 0 0 0 .3-3Z" />
      </>
    ),
  },
  'projects-large': {
    vb: '0 0 56 56',
    el: <path d="M8 8h16v16H8zM32 8h16v16h-16zM8 32h16v16H8zM32 32h16v16h-16z" />,
  },
  // Same real Ikonate glyph as 'facturacion' above, same native 24×24 vb —
  // Ikonate has no separate large-format artwork to pull, and rescaling the
  // real path data by hand would be inventing geometry same as drawing a
  // fresh one. The <Icon> wrapper renders this crisply at size=56 regardless.
  'facturacion-large': {
    vb: '0 0 24 24',
    el: <path d="M12 4L12 6M12 18L12 20M15.5 8C15.1666667 6.66666667 14 6 12 6 9 6 8.5 7.95652174 8.5 9 8.5 13.140327 15.5 10.9649412 15.5 15 15.5 16.0434783 15 18 12 18 10 18 8.83333333 17.3333333 8.5 16" />,
  },
  'messages-large': {
    vb: '0 0 56 56',
    el: (
      <>
        <path d="M8 14h40v24H24L14 46v-8H8V14Z" />
        <path d="M16 22h24M16 30h16" />
      </>
    ),
  },

  // ── Legacy aliases → DS icons ────────────────────────────────────────────
  envelope: {
    vb: '0 0 18 18',
    el: (
      <>
        <rect x="2.5" y="4" width="13" height="10" rx="1" />
        <path d="m3 5 6 4.5L15 5" />
      </>
    ),
  },
  bolt: {
    vb: '0 0 18 18',
    el: <path d="M10 2 4 10h4l-2 6 6-8H8l2-6Z" />,
  },
  person: {
    vb: '0 0 18 18',
    el: (
      <>
        <circle cx="9" cy="6.5" r="3" />
        <path d="M3.5 15.5c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
      </>
    ),
  },

  // ── Legacy utility icons (24×24) ─────────────────────────────────────────
  close: {
    vb: '0 0 24 24',
    el: <path d="M6.343 6.343L17.657 17.657M6.343 17.657L17.657 6.343" />,
  },
  return: {
    vb: '0 0 24 24',
    el: (
      <>
        <path d="M19 8L19 11C19 12.105 18.105 13 17 13L6 13" />
        <polyline points="8 16 5 13 8 10" />
      </>
    ),
  },
  forward: {
    vb: '0 0 24 24',
    el: <path d="M10 14.74L3 19V5l7 4.26V5l12 7-12 7v-4.26z" />,
  },
  ok: {
    vb: '0 0 24 24',
    el: <polyline points="4 13 9 18 20 7" />,
  },
  error: {
    vb: '0 0 24 24',
    el: (
      <>
        <path d="M12 8L12 13" />
        <line x1="12" y1="16" x2="12" y2="16" />
        <circle cx="12" cy="12" r="10" />
      </>
    ),
  },
  'arrow-right': {
    vb: '0 0 24 24',
    el: (
      <>
        <path d="M15 18l6-6-6-6" />
        <path d="M3 12h17" />
      </>
    ),
  },
  flag: {
    vb: '0 0 24 24',
    el: (
      <>
        <path d="M5 21V5" />
        <path d="M19 5s-1.5 2-4 2-4-2-4-2v9s1.5 2 4 2 4-2 4-2V5z" />
      </>
    ),
  },
  inbox: {
    vb: '0 0 24 24',
    el: (
      <>
        <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
        <path d="M5.45 5.11L2 12v6a2 2 0 002 2h16a2 2 0 002-2v-6l-3.45-6.89A2 2 0 0016.76 4H7.24a2 2 0 00-1.79 1.11z" />
      </>
    ),
  },
  activity: {
    vb: '0 0 24 24',
    el: <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />,
  },
  send: {
    vb: '0 0 24 24',
    el: (
      <>
        <line x1="22" y1="2" x2="11" y2="13" />
        <polygon points="22 2 15 22 11 13 2 9 22 2" />
      </>
    ),
  },
  compose: {
    vb: '0 0 24 24',
    el: (
      <>
        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
      </>
    ),
  },
  alert: {
    vb: '0 0 24 24',
    el: (
      <>
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12" y2="17" />
      </>
    ),
  },
  plus: {
    vb: '0 0 24 24',
    el: (
      <>
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </>
    ),
  },

  // ── Social network icons (18×18) ─────────────────────────────────────────
  'social-facebook': {
    vb: '0 0 18 18',
    el: <path d="M11 2H9A4 4 0 0 0 5 6V8.5H3V11h2v7h3V11h2l.5-2.5H8V6a1 1 0 0 1 1-1h2V2Z" />,
  },
  'social-instagram': {
    vb: '0 0 18 18',
    el: (
      <>
        <rect x="2" y="2" width="14" height="14" rx="4" />
        <circle cx="9" cy="9" r="3.5" />
        <path d="M13.5 4.5v.01" />
      </>
    ),
  },
  'social-twitter': {
    vb: '0 0 18 18',
    el: <path d="M3 3L15 15M15 3L3 15" />,
  },
  'social-linkedin': {
    vb: '0 0 18 18',
    el: (
      <>
        <rect x="2" y="2" width="14" height="14" rx="2.5" />
        <path d="M5.5 8V13" />
        <path d="M5.5 5.5v.01" />
        <path d="M9 13V10.5a2.5 2.5 0 0 1 5 0V13M9 8V13" />
      </>
    ),
  },
  'social-youtube': {
    vb: '0 0 18 18',
    el: (
      <>
        <rect x="1.5" y="4" width="15" height="10" rx="2.5" />
        <path d="M7.5 7L12 9 7.5 11V7Z" />
      </>
    ),
  },
  'social-tiktok': {
    vb: '0 0 18 18',
    el: (
      <>
        <path d="M10 3v9.5a3 3 0 1 1-2-2.83" />
        <path d="M10 3h2a3 3 0 0 0 3 3" />
      </>
    ),
  },

  pencil: {
    vb: '0 0 24 24',
    el: <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />,
  },
  'chevron-left': {
    vb: '0 0 24 24',
    el: <path d="M15 18l-6-6 6-6" />,
  },
  sparkle: {
    vb: '0 0 24 24',
    el: (
      <>
        <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
        <path d="M18 14l.75 2.25L21 17l-2.25.75L18 20l-.75-2.25L15 17l2.25-.75L18 14z" />
      </>
    ),
  },
  blockquote: {
    vb: '0 0 24 24',
    el: (
      <>
        <rect x="3" y="4" width="2" height="16" rx="1" />
        <line x1="8" y1="8" x2="21" y2="8" />
        <line x1="8" y1="14" x2="18" y2="14" />
      </>
    ),
  },
  code: {
    vb: '0 0 24 24',
    el: (
      <>
        <path d="m8 8-4 4 4 4" />
        <path d="m16 8 4 4-4 4" />
      </>
    ),
  },
  link: {
    vb: '0 0 24 24',
    el: (
      <>
        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
      </>
    ),
  },
  paperclip: {
    vb: '0 0 24 24',
    el: <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />,
  },
  'list-bullet': {
    vb: '0 0 24 24',
    el: (
      <>
        <line x1="9" y1="6" x2="20" y2="6" />
        <line x1="9" y1="12" x2="20" y2="12" />
        <line x1="9" y1="18" x2="20" y2="18" />
        <circle cx="4" cy="6" r="1.5" />
        <circle cx="4" cy="12" r="1.5" />
        <circle cx="4" cy="18" r="1.5" />
      </>
    ),
  },
  'list-ordered': {
    vb: '0 0 24 24',
    el: (
      <>
        <line x1="10" y1="6" x2="21" y2="6" />
        <line x1="10" y1="12" x2="21" y2="12" />
        <line x1="10" y1="18" x2="21" y2="18" />
        <polyline points="3 6 4 5 4 9" />
        <path d="M3 17c0-1 .5-2 2-2s2 .5 2 1-.5 1-2 2h2" />
      </>
    ),
  },

  // ── Voice (Fase 4) ────────────────────────────────────────────────────────
  mic: {
    vb: '0 0 24 24',
    el: (
      <>
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="23" />
        <line x1="8" y1="23" x2="16" y2="23" />
      </>
    ),
  },
  volume: {
    vb: '0 0 24 24',
    el: (
      <>
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
        <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
        <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
      </>
    ),
  },
  'volume-mute': {
    vb: '0 0 24 24',
    el: (
      <>
        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
        <line x1="23" y1="9" x2="17" y2="15" />
        <line x1="17" y1="9" x2="23" y2="15" />
      </>
    ),
  },
  stop: {
    vb: '0 0 24 24',
    el: <rect x="6" y="6" width="12" height="12" rx="1.5" />,
  },
};

interface IconProps {
  name: IconName;
  size?: number;
  strokeWidth?: number;
  className?: string;
  'aria-label'?: string;
}

export function Icon({
  name,
  size = 18,
  strokeWidth = 1.5,
  className,
  'aria-label': ariaLabel,
}: IconProps) {
  const { vb, el } = ICONS[name];
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox={vb}
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden={!ariaLabel}
      aria-label={ariaLabel}
    >
      {el}
    </svg>
  );
}
